import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteChat, getDeleteChatPreflight } from '../api/client';
import type { ChatType } from '../types';

interface Props {
  chatId: string;
  projectName: string | null;
  chatType: ChatType;
}

export function DangerZone({ chatId, projectName, chatType }: Props) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [alsoLeave, setAlsoLeave] = useState(false);
  const [acks, setAcks] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const qc = useQueryClient();

  const preflight = useQuery({
    queryKey: ['delete-preflight', chatId],
    queryFn: () => getDeleteChatPreflight(chatId),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () => deleteChat(chatId, { confirmChatId: confirm, alsoLeaveChat: alsoLeave }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['chats'] });
      navigate('/chats?deleted=' + encodeURIComponent(chatId));
    },
  });

  // Reset modal state when reopened.
  useEffect(() => {
    if (open) { setConfirm(''); setAlsoLeave(false); setAcks({}); mutation.reset(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const blockers = preflight.data?.warnings.filter(w => w.severity === 'block') ?? [];
  const warns    = preflight.data?.warnings.filter(w => w.severity === 'warn')  ?? [];
  const allAcked = warns.every(w => acks[w.code]);
  const confirmsMatch = confirm === chatId;
  const canSubmit = !mutation.isPending && blockers.length === 0 && allAcked && confirmsMatch;

  return (
    <section className="mt-8 rounded-lg border-2 border-red-200 bg-red-50 p-5" data-testid="danger-zone">
      <h3 className="text-sm font-semibold text-red-800">Danger zone</h3>
      <p className="mt-2 text-sm text-red-700">
        Permanently remove this chat from the bot. Deletes settings, schedule, queries, reports,
        events, S3 caches, and stops in-flight workflows. Stripe subscriptions are{' '}
        <strong>not</strong> auto-cancelled.
      </p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-4 rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
        data-testid="open-delete-modal"
      >
        ⚠️ Delete this chat
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-modal"
          >
            <h4 className="text-lg font-semibold text-gray-900">
              Delete <code className="rounded bg-gray-100 px-1 font-mono text-sm">{chatId}</code>?
            </h4>
            {projectName && (
              <p className="mt-1 text-sm text-gray-600">Project: <strong>{projectName}</strong></p>
            )}

            {preflight.isLoading && <p className="mt-4 text-sm text-gray-500">Loading checks…</p>}

            {preflight.data && (
              <div className="mt-4 space-y-3">
                {blockers.length > 0 && (
                  <div className="rounded-md border-2 border-red-300 bg-red-50 p-3 text-sm text-red-800">
                    <strong>Cannot delete — fix these first:</strong>
                    <ul className="mt-1 list-inside list-disc">
                      {blockers.map(b => <li key={b.code}>{b.message}</li>)}
                    </ul>
                  </div>
                )}
                {warns.length > 0 && (
                  <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                    <strong>Acknowledge these warnings:</strong>
                    {warns.map(w => (
                      <label key={w.code} className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={!!acks[w.code]}
                          onChange={(e) => setAcks({ ...acks, [w.code]: e.target.checked })}
                          data-testid={`ack-${w.code}`}
                        />
                        <span>{w.message}</span>
                      </label>
                    ))}
                  </div>
                )}
                {preflight.data.canSelfLeave && chatType !== 'private' && (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={alsoLeave}
                      onChange={(e) => setAlsoLeave(e.target.checked)}
                      data-testid="also-leave-chat"
                    />
                    Also leave the Telegram chat (bot self-removes)
                  </label>
                )}
                {chatType === 'private' && (
                  <p className="text-xs text-gray-500">Private DM — bot cannot self-leave.</p>
                )}
              </div>
            )}

            <div className="mt-5">
              <label className="block text-sm font-medium text-gray-700">
                To confirm, type the chat ID:
              </label>
              <input
                type="text"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={chatId}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 font-mono text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                data-testid="confirm-chat-id"
              />
            </div>

            {mutation.isError && (
              <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-2 text-sm text-red-800">
                {(mutation.error as Error).message}
              </div>
            )}
            {mutation.isSuccess && (
              <div className="mt-3 rounded-md border border-emerald-300 bg-emerald-50 p-2 text-sm text-emerald-800">
                Deleted. Redirecting…
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={mutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => mutation.mutate()}
                disabled={!canSubmit}
                className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="submit-delete"
              >
                {mutation.isPending ? 'Deleting…' : 'DELETE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
